/**
 * Global Design Tokens
 *
 * This file contains all design tokens used throughout the application.
 * These tokens ensure consistency and make it easier to maintain the design system.
 */

import { cn } from '@/lib/utils';

/**
 * Core spacing tokens based on a 4px grid
 */
export const spacing = {
  0: '0',
  px: 'px',
  0.5: '0.5', // 2px
  1: '1', // 4px
  1.5: '1.5', // 6px
  2: '2', // 8px
  2.5: '2.5', // 10px
  3: '3', // 12px
  3.5: '3.5', // 14px
  4: '4', // 16px
  5: '5', // 20px
  6: '6', // 24px
  7: '7', // 28px
  8: '8', // 32px
  9: '9', // 36px
  10: '10', // 40px
  12: '12', // 48px
  14: '14', // 56px
  16: '16', // 64px
  20: '20', // 80px
  24: '24' // 96px
} as const;

/**
 * Component size tokens
 */
export const sizes = {
  // Icon sizes
  icon: {
    xs: 'h-3 w-3', // 12px
    sm: 'h-4 w-4', // 16px - Most common
    md: 'h-5 w-5', // 20px
    lg: 'h-6 w-6', // 24px
    xl: 'h-8 w-8' // 32px
  },

  // Button sizes (use with Button component's size prop when possible)
  button: {
    xs: 'h-7 px-2',
    sm: 'h-8 px-3',
    md: 'h-9 px-4',
    lg: 'h-10 px-6',
    icon: 'size-8', // Square icon button
    iconSm: 'size-7',
    iconLg: 'size-9'
  },

  // Avatar sizes
  avatar: {
    xs: 'h-6 w-6',
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16'
  }
} as const;

/**
 * Typography tokens
 */
export const typography = {
  // Text sizes
  text: {
    xs: 'text-xs', // 12px
    sm: 'text-sm', // 14px
    base: 'text-base', // 16px
    lg: 'text-lg', // 18px
    xl: 'text-xl', // 20px
    '2xl': 'text-2xl', // 24px
    '3xl': 'text-3xl', // 30px
    '4xl': 'text-4xl' // 36px
  },

  // Font weights
  weight: {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold'
  },

  // Line heights
  leading: {
    none: 'leading-none',
    tight: 'leading-tight',
    snug: 'leading-snug',
    normal: 'leading-normal',
    relaxed: 'leading-relaxed',
    loose: 'leading-loose'
  },

  // Common combinations
  heading: {
    h1: 'text-4xl font-bold tracking-tight',
    h2: 'text-3xl font-semibold tracking-tight',
    h3: 'text-2xl font-semibold tracking-tight',
    h4: 'text-xl font-semibold',
    h5: 'text-lg font-medium',
    h6: 'text-base font-medium'
  },

  body: {
    large: 'text-lg',
    base: 'text-base',
    small: 'text-sm',
    muted: 'text-sm text-muted-foreground'
  },

  // Form labels
  label: 'text-sm font-medium',
  formLabel: 'text-sm font-medium text-muted-foreground',
  caption: 'text-xs text-muted-foreground'
} as const;

/**
 * Layout tokens
 */
export const layout = {
  // Container padding
  container: {
    base: 'px-4 lg:px-6',
    tight: 'px-3 lg:px-4',
    loose: 'px-6 lg:px-8'
  },

  // Section spacing
  section: {
    gap: 'gap-6',
    gapTight: 'gap-4',
    gapLoose: 'gap-8'
  },

  // Grid layouts
  grid: {
    cols2: 'grid grid-cols-2',
    cols3: 'grid grid-cols-3',
    cols4: 'grid grid-cols-4',
    responsive: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
  },

  // Common flex patterns
  flex: {
    center: 'flex items-center justify-center',
    between: 'flex items-center justify-between',
    start: 'flex items-center justify-start',
    end: 'flex items-center justify-end',
    col: 'flex flex-col'
  }
} as const;

/**
 * Border radius tokens
 */
export const radius = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  base: 'rounded',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  full: 'rounded-full'
} as const;

/**
 * Animation tokens
 */
export const animation = {
  // Duration tokens (in milliseconds)
  duration: {
    instant: '0ms',
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
    slower: '700ms'
  },

  // Easing curves
  easing: {
    linear: 'linear',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
    // Custom spring-like curves
    spring: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
  },

  // Tailwind animation classes
  classes: {
    fadeIn: 'animate-in fade-in',
    fadeOut: 'animate-out fade-out',
    slideInFromTop: 'animate-in slide-in-from-top',
    slideInFromBottom: 'animate-in slide-in-from-bottom',
    slideInFromLeft: 'animate-in slide-in-from-left',
    slideInFromRight: 'animate-in slide-in-from-right',
    slideOutToTop: 'animate-out slide-out-to-top',
    slideOutToBottom: 'animate-out slide-out-to-bottom',
    slideOutToLeft: 'animate-out slide-out-to-left',
    slideOutToRight: 'animate-out slide-out-to-right',
    zoomIn: 'animate-in zoom-in-95',
    zoomOut: 'animate-out zoom-out-95'
  }
} as const;

/**
 * Semantic color tokens for specific component states
 */
export const colors = {
  // Progress/Status indicators
  progress: {
    notStarted: 'bg-muted border border-border-strong',
    inProgress: 'bg-chart-2 hover:bg-chart-2/80',
    completed: 'bg-chart-1 hover:bg-chart-1/80'
  },

  // State indicators
  state: {
    success:
      'text-success-subtle-foreground bg-success-subtle border-success/20',
    warning:
      'text-warning-subtle-foreground bg-warning-subtle border-warning/20',
    error: 'text-error-subtle-foreground bg-error-subtle border-error/20',
    info: 'text-info-subtle-foreground bg-info-subtle border-info/20'
  },

  // Form input colors
  form: {
    // Input background using #737373 with 7% opacity
    inputBg: 'bg-input-bg',
    // Label text color using #737373
    labelText: 'text-muted-foreground'
  },

  // Sidebar colors
  sidebar: {
    // Subtle off-white background for visual separation
    // Uses CSS variable --sidebar-background defined in globals.css
    // Light: #FBFBFB (HSL: 0 0% 98.4%)
    // Dark: gray-950
    background: 'bg-sidebar'
  }
} as const;

/**
 * Common patterns and utilities
 */
export const patterns = {
  // Icon with text spacing
  iconSpacing: {
    left: 'mr-2',
    right: 'ml-2'
  },

  // Truncation
  truncate: {
    base: 'truncate',
    small: 'max-w-[150px] truncate',
    medium: 'max-w-[200px] truncate',
    large: 'max-w-[300px] truncate'
  },

  // Common button patterns
  buttonIcon: {
    base: cn(sizes.button.icon, 'p-0'),
    small: cn(sizes.button.iconSm, 'p-0'),
    withGhost: cn(sizes.button.icon, 'p-0', 'hover:bg-transparent')
  },

  // Table patterns
  table: {
    wrapper: 'overflow-hidden rounded-lg border',
    header: 'sticky top-0 z-10 bg-muted',
    cell: 'px-4 py-2',
    sortButton: 'h-auto p-0 font-medium hover:bg-transparent'
  },

  // Form patterns
  form: {
    // Standard label for form inputs
    label: 'text-sm font-medium text-muted-foreground',
    // Standard input styling - increased horizontal padding for better readability
    input: 'h-auto rounded-md border-0 bg-input-bg px-3 py-1.5 text-sm',
    // Label to input spacing
    spacing: 'space-y-2'
  }
} as const;

/**
 * Helper functions
 */
export const tokens = {
  spacing,
  sizes,
  typography,
  layout,
  radius,
  animation,
  colors,
  patterns,

  // Utility functions
  icon: (size: keyof typeof sizes.icon = 'sm') => sizes.icon[size],
  text: (size: keyof typeof typography.text = 'base') => typography.text[size],
  heading: (level: keyof typeof typography.heading) =>
    typography.heading[level],

  // Common combinations
  iconWithText: (iconPosition: 'left' | 'right' = 'right') =>
    cn(sizes.icon.sm, patterns.iconSpacing[iconPosition]),

  pageContainer: (variant: keyof typeof layout.container = 'base') =>
    layout.container[variant],

  buttonWithIcon: (size: keyof typeof sizes.button = 'sm') =>
    cn(sizes.button[size], 'inline-flex items-center gap-2'),

  // Progress state helper
  progressState: (state: keyof typeof colors.progress) => colors.progress[state]
} as const;

// Type exports for TypeScript support
export type SpacingToken = keyof typeof spacing;
export type IconSize = keyof typeof sizes.icon;
export type TextSize = keyof typeof typography.text;
export type HeadingLevel = keyof typeof typography.heading;
